/*
  CPF, CNPJ, telefone e CEP: validação e máscara. A mesma regra do banco
  (`documento_valido`), aqui para avisar enquanto a pessoa digita -- no
  cadastro interno e na página pública do link.
*/

export const soDigitos = (v: string | null | undefined) => (v ?? '').replace(/\D/g, '');

export function cpfValido(v: string): boolean {
  const d = soDigitos(v);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dv = (n: number) => {
    let soma = 0;
    for (let i = 0; i < n; i++) soma += Number(d[i]) * (n + 1 - i);
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
}

export function cnpjValido(v: string): boolean {
  const d = soDigitos(v);
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const dv = (n: number) => {
    const pesos = n === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = pesos.reduce((a, p, i) => a + Number(d[i]) * p, 0);
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return dv(12) === Number(d[12]) && dv(13) === Number(d[13]);
}

export type TipoPessoa = 'PF' | 'PJ';

/** Mensagem de erro do documento, ou null se está vazio ou certo. */
export function problemaDoDocumento(tipo: TipoPessoa, v: string): string | null {
  const d = soDigitos(v);
  if (!d) return null;
  if (tipo === 'PF') {
    if (d.length !== 11) return 'CPF tem 11 dígitos.';
    return cpfValido(d) ? null : 'CPF inválido: confira os números.';
  }
  if (d.length !== 14) return 'CNPJ tem 14 dígitos.';
  return cnpjValido(d) ? null : 'CNPJ inválido: confira os números.';
}

export function mascaraDocumento(tipo: TipoPessoa, v: string): string {
  const d = soDigitos(v).slice(0, tipo === 'PF' ? 11 : 14);
  if (tipo === 'PF') {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

export function mascaraTelefone(v: string): string {
  const d = soDigitos(v).slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function mascaraCep(v: string): string {
  const d = soDigitos(v).slice(0, 8);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** Só os dígitos, sem o 55 do Brasil na frente: "+55 68 99938-8770" -> "68999388770". */
export const telefoneSemPais = (v: string | null | undefined) => {
  const d = soDigitos(v);
  return (d.length === 12 || d.length === 13) && d.startsWith('55') ? d.slice(2) : d;
};

/** Telefone com DDD: 10 ou 11 dígitos. */
export const telefoneValido = (v: string) => {
  const d = soDigitos(v);
  return !d || d.length === 10 || d.length === 11;
};

export const emailValido = (v: string) => !v.trim() || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim());

export const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR',
  'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
] as const;

/** Endereço pelo CEP (BrasilAPI). Funciona no navegador e no servidor. */
export async function buscarCep(cep: string): Promise<{ logradouro: string; bairro: string; cidade: string; estado: string } | null> {
  const d = soDigitos(cep);
  if (d.length !== 8) return null;
  try {
    const r = await fetch(`https://brasilapi.com.br/api/cep/v1/${d}`, { cache: 'no-store' });
    if (!r.ok) return null;
    const j = await r.json();
    return { logradouro: j.street ?? '', bairro: j.neighborhood ?? '', cidade: j.city ?? '', estado: j.state ?? '' };
  } catch {
    return null;
  }
}

/** Link de WhatsApp para um número com DDD (Brasil). */
export const linkWhatsApp = (numero: string, texto?: string) => {
  const d = soDigitos(numero);
  const n = d.length <= 11 ? `55${d}` : d;
  return `https://wa.me/${n}${texto ? `?text=${encodeURIComponent(texto)}` : ''}`;
};
